import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  CalendarClock, CheckCircle2, Clock, DollarSign, History, Loader2, Pencil, RotateCcw, Users, Zap,
} from 'lucide-react';
import api from '../../utils/api';
import { PAGE, SettingsHeader, Switch } from './SettingsUI';
import { Dialog, Field, FormRow, TableSkeleton, ErrorState, plural } from '../../components/ui';
import { Notice } from '../Financials/financeKit';
import { useConfirm } from '../../components/ConfirmDialog';
import { useFeatureReload } from '../../context/FeatureContext';
import usePayPermissions, { needsPermission } from '../../hooks/usePayPermissions';
import {
  RATES, RateLabel, RateInputs, ratesForm, parseRates, rateText, dmy, todayIso, errText,
  TIME_OPTIONS, hm, timeLabel,
} from './payRatesKit';

// Settings > Pay Rates (owner spec v1.2, sections 4.1 to 4.3). The company's default rates as
// dated versions, the evening window, and the switch that moves the company from SCHADS pay
// groups to these rates. Every figure comes from /api/pay-rates; nothing is calculated here
// except which inputs changed.

const TONES = {
  emerald: 'bg-emerald-50 text-emerald-600',
  indigo: 'bg-indigo-50 text-indigo-600',
  amber: 'bg-amber-50 text-amber-600',
  violet: 'bg-violet-50 text-violet-600',
};

function Card({ title, icon: Icon, tone = 'emerald', actions, children, bodyClass = 'p-5', sub }) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={clsx('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', TONES[tone])}><Icon size={15} aria-hidden="true" /></span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-slate-800">{title}</h2>
            {sub && <p className="text-[12px] text-slate-500">{sub}</p>}
          </div>
        </div>
        {actions}
      </div>
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

/* ── Edit default rates ─────────────────────────────────────────────────── */

function DefaultsDialog({ open, current, onClose, onSaved }) {
  const [form, setForm] = useState(() => ratesForm(current));
  const [startDate, setStartDate] = useState(todayIso());
  const [step, setStep] = useState('edit');      // 'edit' | 'confirm'
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(ratesForm(current));
    setStartDate(todayIso());
    setStep('edit');
    setCount(null);
    setError('');
  }, [open, current]);

  const close = () => { if (!busy) onClose(); };

  const review = async () => {
    if (!startDate) { setError('Pick the date the new rates start.'); return; }
    const { error: bad } = parseRates(form);
    if (bad) { setError(bad); return; }
    setError('');
    setBusy(true);
    try {
      const { data } = await api.get(`/pay-rates/defaults/preview?start_date=${encodeURIComponent(startDate)}`);
      setCount(Number(data && data.staff_count) || 0);
      setStep('confirm');
    } catch (e) { setError(errText(e, "We couldn't count the staff profiles. Try again.")); }
    finally { setBusy(false); }
  };

  const save = async () => {
    const { rates, error: bad } = parseRates(form);
    if (bad) { setError(bad); setStep('edit'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/pay-rates/defaults', { start_date: startDate, ...rates });
      const n = Number(data && data.staff_updated);
      toast.success(Number.isFinite(n) ? `Default rates saved. ${plural(n, 'staff profile')} updated.` : 'Default rates saved');
      setBusy(false);
      onSaved();
      onClose();
    } catch (e) {
      setError(errText(e, "The default rates weren't saved. Try again."));
      setBusy(false);
    }
  };

  const changed = RATES.filter((r) => current && Number(form[r.key]) !== Number(current[r.key]));
  const past = startDate && startDate < todayIso();

  return (
    <Dialog open={open} onClose={close} size="lg" dismissable={!busy}
      title={step === 'edit' ? 'Edit default rates' : 'Confirm the new default rates'}
      description={step === 'edit'
        ? 'Saves a new version with a start date. Earlier versions stay in the history.'
        : 'Check the change before it is saved to every staff profile.'}
      footer={step === 'edit' ? (
        <>
          <button type="button" className="btn-secondary" onClick={close} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" onClick={review} disabled={busy}>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : null}Next
          </button>
        </>
      ) : (
        <>
          <button type="button" className="btn-secondary" onClick={() => setStep('edit')} disabled={busy}>Back</button>
          <button type="button" className="btn-primary" onClick={save} disabled={busy}>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : null}Confirm
          </button>
        </>
      )}>
      {step === 'edit' ? (
        <div className="space-y-4">
          <FormRow>
            <Field label="Start date" required hint="Any date, including a past date. The new rates apply to shifts on or after it.">
              {(id) => <input id={id} type="date" className="input-field" value={startDate} onChange={(e) => { setStartDate(e.target.value); setError(''); }} />}
            </Field>
          </FormRow>
          <RateInputs value={form} onChange={(v) => { setForm(v); setError(''); }} compare={current || undefined} compareLabel="Current" idPrefix="def" disabled={busy} />
          {error && <p className="text-[13px] text-rose-600" role="alert">{error}</p>}
        </div>
      ) : (
        <div className="space-y-4">
          <Notice tone="amber" title={`${plural(count || 0, 'staff profile')} will change`}>
            Every staff profile gets a new rate version starting {dmy(startDate)}, employees and contractors, including staff whose rates were edited on their profile.
            {past ? ' Because the start date is in the past, pay lines from that date that have not been sent are recalculated at the new rates.' : ''}
          </Notice>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/60 text-[12px] font-semibold text-slate-500">
                  <th scope="col" className="px-4 py-2 text-left">Rate</th>
                  <th scope="col" className="px-4 py-2 text-right">Current</th>
                  <th scope="col" className="px-4 py-2 text-right">New</th>
                </tr>
              </thead>
              <tbody>
                {RATES.map((r) => {
                  const diff = changed.includes(r);
                  return (
                    <tr key={r.key} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-2"><RateLabel rate={r} /></td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">{rateText(current && current[r.key])}</td>
                      <td className={clsx('px-4 py-2 text-right tabular-nums', diff ? 'font-semibold text-primary-700' : 'text-slate-800')}>{rateText(form[r.key])}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {error && <p className="text-[13px] text-rose-600" role="alert">{error}</p>}
        </div>
      )}
    </Dialog>
  );
}

/* ── Turn on Pay Rates ──────────────────────────────────────────────────── */

function SwitchDialog({ open, onClose, onSwitched }) {
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [cutover, setCutover] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Workers who work shifts but have no pay group would start on $0: the admin picks a pay group to
  // start them from, and the switch is refused until nobody is left on $0.
  const [fallback, setFallback] = useState('');
  const [groups, setGroups] = useState([]);

  // The starting rates depend on the cut-over date (the award rate in force then), so the preview is
  // fetched again whenever the date changes.
  const load = useCallback((cut, group) => {
    setData(null);
    setLoadErr('');
    api.get(`/pay-rates/switch/preview?cutover_date=${encodeURIComponent(cut)}${group ? `&fallback_group=${encodeURIComponent(group)}` : ''}`)
      .then((r) => setData(r.data || { workers: [] }))
      .catch((e) => setLoadErr(errText(e, "We couldn't load the preview. Try again.")));
  }, []);

  useEffect(() => {
    if (!open) return;
    setError(''); setCutover(todayIso()); setFallback('');
    api.get('/pay-groups').then((r) => setGroups(Array.isArray(r.data) ? r.data.filter((g) => g.is_active !== false) : [])).catch(() => setGroups([]));
  }, [open]);
  useEffect(() => {
    if (!open || !cutover || cutover < todayIso()) return undefined;
    const t = setTimeout(() => load(cutover, fallback), 300);
    return () => clearTimeout(t);
  }, [open, cutover, fallback, load]);

  const close = () => { if (!busy) onClose(); };
  const workers = (data && Array.isArray(data.workers)) ? data.workers.filter((w) => !w.skip) : [];
  const noRate = (data && Array.isArray(data.no_rate)) ? data.no_rate : [];
  const needsGroup = noRate.length > 0 || !!fallback;

  const confirmSwitch = async () => {
    if (!cutover) { setError('Pick the cut-over date.'); return; }
    if (cutover < todayIso()) { setError('The cut-over date cannot be in the past.'); return; }
    if (noRate.length) { setError(`${plural(noRate.length, 'worker')} would start on $0. Choose a pay group to start them from.`); return; }
    setBusy(true);
    try {
      await api.post('/pay-rates/switch', { cutover_date: cutover, fallback_group: fallback || undefined });
      toast.success(`Pay Rates are on from ${dmy(cutover)}`);
      setBusy(false);
      onSwitched();
      onClose();
    } catch (e) {
      setError(errText(e, "Pay Rates weren't turned on. Try again."));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} size="full" dismissable={!busy}
      title="Use Pay Rates"
      description="Check each worker's starting rates. They are worked out from today's pay groups so one hour pays the same as it does now."
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={close} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" onClick={confirmSwitch} disabled={busy || !data || noRate.length > 0}>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Zap size={14} aria-hidden="true" />}Confirm
          </button>
        </>
      )}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Cut-over date" required hint="Today or later. Shifts on or after this date are paid from Pay Rates; earlier shifts keep their award pay.">
            {(id) => <input id={id} type="date" className="input-field w-48" min={todayIso()} value={cutover} onChange={(e) => { setCutover(e.target.value); setError(''); }} />}
          </Field>
          {needsGroup && (
            <Field label="Start workers with no pay group from" hint="Used only for workers who have shifts but no pay group, override rate or contractor rate card.">
              {(id) => (
                <select id={id} className="input-field w-80" value={fallback} onChange={(e) => { setFallback(e.target.value); setError(''); }}>
                  <option value="">Choose a pay group</option>
                  {groups.map((g) => <option key={g.id || g.name} value={g.name}>{g.name}</option>)}
                </select>
              )}
            </Field>
          )}
        </div>
        {error && <p className="text-[13px] text-rose-600" role="alert">{error}</p>}
        {noRate.length > 0 && (
          <Notice tone="amber">
            {plural(noRate.length, 'worker')} with shifts {noRate.length === 1 ? 'has' : 'have'} no pay rate to start from and would be paid $0: {noRate.slice(0, 6).map((w) => w.name).join(', ')}{noRate.length > 6 ? ` and ${noRate.length - 6} more` : ''}. Choose a pay group to start them from, or give them a pay group on their profile first.
          </Notice>
        )}

        {loadErr ? (
          <div className="rounded-xl border border-slate-200"><ErrorState message={loadErr} onRetry={() => load(cutover, fallback)} /></div>
        ) : !data ? (
          <div className="rounded-xl border border-slate-200"><TableSkeleton rows={5} cols={8} /></div>
        ) : workers.length === 0 ? (
          <p className="rounded-xl border border-slate-200 px-4 py-6 text-center text-sm text-slate-500">No workers to set up. New staff get the default rates when they are added.</p>
        ) : (
          <>
            <p className="text-[13px] text-slate-600">
              {plural(workers.length, 'worker')}. Each cell shows the starting rate, and underneath, what one hour pays today. A difference is marked in amber.
            </p>
            <div className="max-h-[52vh] overflow-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[980px] text-sm">
                <thead className="sticky top-0 z-[1] bg-white">
                  <tr className="border-b border-slate-200 text-[12px] font-semibold text-slate-500">
                    <th scope="col" className="sticky left-0 bg-white px-4 py-2 text-left">Worker</th>
                    {RATES.map((r) => (
                      <th key={r.key} scope="col" className="px-3 py-2 text-right whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          <span className={clsx('h-2 w-2 rounded-full', r.dot)} aria-hidden="true" />{r.label}
                        </span>
                        <span className="block text-[11px] font-normal text-slate-400">$/{r.unit}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {workers.map((w) => {
                    const seeded = w.seeded || {};
                    const now = w.today || {};
                    return (
                      <tr key={w.user_id} className="border-b border-slate-100 last:border-0">
                        <td className="sticky left-0 bg-white px-4 py-2">
                          <span className="font-medium text-slate-800">{w.name || 'Unnamed worker'}</span>
                          {(w.employment_type || w.pay_group) && (
                            <span className="block text-[12px] text-slate-500">{[w.employment_type && String(w.employment_type).replace(/_/g, ' '), w.pay_group].filter(Boolean).join(', ')}</span>
                          )}
                        </td>
                        {RATES.map((r) => {
                          const has = now[r.key] !== undefined && now[r.key] !== null && now[r.key] !== '';
                          const diff = has && Math.round(Number(now[r.key]) * 100) !== Math.round(Number(seeded[r.key]) * 100);
                          return (
                            <td key={r.key} className={clsx('px-3 py-2 text-right tabular-nums', diff && 'bg-amber-50')}>
                              <span className="font-semibold text-slate-900">{rateText(seeded[r.key])}</span>
                              {has && <span className={clsx('block text-[11.5px]', diff ? 'font-medium text-amber-800' : 'text-slate-500')}>Today {rateText(now[r.key])}</span>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Notice tone="slate">
              On Confirm, each worker gets these rates as their first rate version, starting {dmy(cutover)}. You can edit any worker's rates afterwards on their profile.
            </Notice>
          </>
        )}
      </div>
    </Dialog>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */

export default function PayRates() {
  const confirm = useConfirm();
  const reloadFeatures = useFeatureReload();
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [rules, setRules] = useState({ evening_start: '18:00', evening_end: '24:00' });
  const [savingRules, setSavingRules] = useState(false);
  const [switchingBack, setSwitchingBack] = useState(false);
  // Pay Rates: Settings changes the defaults, the time rules and the switch (spec section 10).
  const { can } = usePayPermissions();
  const canSettings = can('pay_rates.settings');

  const load = useCallback(() => {
    setLoadErr('');
    api.get('/pay-rates/settings')
      .then((r) => {
        const d = r.data || {};
        setData(d);
        const tr = d.time_rules || {};
        setRules({ evening_start: hm(tr.evening_start) || '18:00', evening_end: hm(tr.evening_end) || '24:00' });
      })
      .catch((e) => setLoadErr(errText(e, "We couldn't load the pay rates. Check your connection, then try again.")));
  }, []);
  useEffect(() => { load(); }, [load]);

  const defaults = data && data.defaults ? data.defaults : null;
  const history = useMemo(() => {
    const h = data && Array.isArray(data.history) ? [...data.history] : [];
    return h.sort((a, b) => String(b.start_date).localeCompare(String(a.start_date)) || String(b.created_at || '').localeCompare(String(a.created_at || '')));
  }, [data]);
  const isRates = data && data.engine === 'rates';
  const savedRules = data && data.time_rules ? data.time_rules : {};
  const rulesDirty = hm(savedRules.evening_start || '18:00') !== rules.evening_start || hm(savedRules.evening_end || '24:00') !== rules.evening_end;
  const canSwitchBack = data && data.can_switch_back !== false && data.switch_back_allowed !== false;

  const saveRules = async () => {
    if (rules.evening_start >= rules.evening_end) { toast.error('The evening window must end after it starts.'); return; }
    setSavingRules(true);
    try {
      await api.put('/pay-rates/time-rules', { evening_start: rules.evening_start, evening_end: rules.evening_end });
      toast.success('Time rules saved');
      load();
    } catch (e) { toast.error(errText(e, "The time rules weren't saved. Try again.")); }
    finally { setSavingRules(false); }
  };

  const afterSwitch = () => { load(); if (reloadFeatures) reloadFeatures(); };

  const switchBack = async () => {
    const ok = await confirm(
      'Shifts from the cut-over date go back to being paid from SCHADS pay groups. Pay lines already worked out by Pay Rates are set aside. This is only possible while no pay line has been sent to Xero.',
      { title: 'Switch back to SCHADS pay groups?', confirmLabel: 'Switch back', danger: true });
    if (!ok) return;
    setSwitchingBack(true);
    try {
      await api.post('/pay-rates/switch/back');
      toast.success('This company pays from SCHADS pay groups again');
      afterSwitch();
    } catch (e) { toast.error(errText(e, "We couldn't switch back. Try again.")); }
    finally { setSwitchingBack(false); }
  };

  if (loadErr && !data) {
    return (
      <div className={PAGE}>
        <SettingsHeader title="Pay Rates" description="Default pay rates, the evening window and the switch from SCHADS pay groups." />
        <div className="rounded-xl border border-slate-200 bg-white"><ErrorState message={loadErr} onRetry={load} /></div>
      </div>
    );
  }

  return (
    <div className={PAGE}>
      <SettingsHeader
        title="Pay Rates"
        description="Default pay rates for every worker, the evening window, and the switch from SCHADS pay groups to these rates."
        actions={data && (
          <span className={clsx('inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12.5px] font-medium ring-1 ring-inset',
            isRates ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : 'bg-slate-100 text-slate-700 ring-slate-200')}>
            <span className={clsx('h-2 w-2 rounded-full', isRates ? 'bg-emerald-500' : 'bg-slate-400')} aria-hidden="true" />
            {isRates ? 'Paying from Pay Rates' : 'Paying from SCHADS pay groups'}
          </span>
        )}
      />

      {!data ? (
        <div className="rounded-xl border border-slate-200 bg-white"><TableSkeleton rows={6} cols={4} /></div>
      ) : (
        <>
          {!canSettings && (
            <Notice tone="slate" title="You can see these settings">
              Changing the default rates, the time rules or the switch needs the Pay Rates: Settings permission. Ask your company admin to add it to your role.
            </Notice>
          )}
          {/* The switch */}
          <Card title="Use Pay Rates" icon={Zap} tone="violet" sub="Which pay calculation this company uses">
            {isRates ? (
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">This company pays from Pay Rates since {dmy(data.cutover_date)}</p>
                    <p className="mt-1 text-[13px] leading-relaxed text-slate-600">Shifts before that date keep their award pay. Each worker's rates are on their profile, under Pay Rates.</p>
                    {!canSwitchBack && (
                      <p className="mt-1 text-[12.5px] text-slate-500">Switching back is no longer possible because pay lines have been sent to Xero.</p>
                    )}
                  </div>
                </div>
                {canSwitchBack && canSettings && (
                  <button type="button" className="btn-secondary btn-sm" onClick={switchBack} disabled={switchingBack}>
                    {switchingBack ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <RotateCcw size={13} aria-hidden="true" />}Switch back
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 max-w-2xl">
                  <p className="text-sm font-semibold text-slate-900">This company pays from SCHADS pay groups</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
                    Turn this on to pay every worker from their own rate card: Weekday, Weekday Evening, Saturday, Sunday, Public Holiday, Sleepover and Km.
                    You see each worker's starting rates and pick a cut-over date before anything changes. Pay groups stay in <Link to="/settings/pay-groups" className="font-medium text-primary-700 hover:underline">Settings, Pay Groups</Link>.
                  </p>
                </div>
                {canSettings && (
                  <div className="flex items-center gap-3">
                    <span className="text-[13px] font-medium text-slate-700">Use Pay Rates</span>
                    <Switch checked={false} onChange={() => setSwitchOpen(true)} label="Use Pay Rates" />
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Default rates */}
          <Card title="Default Rates" icon={DollarSign} tone="emerald"
            sub={defaults && defaults.start_date ? `In force since ${dmy(defaults.start_date)}` : 'Not set yet'}
            actions={canSettings ? (
              <button type="button" className="btn-secondary btn-sm" onClick={() => setEditOpen(true)}>
                <Pencil size={13} aria-hidden="true" />Edit
              </button>
            ) : null}
            bodyClass="">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[12px] font-semibold text-slate-500">
                    <th scope="col" className="px-5 py-2.5 text-left">Rate</th>
                    <th scope="col" className="px-4 py-2.5 text-right">Amount</th>
                    <th scope="col" className="hidden px-5 py-2.5 text-left sm:table-cell">Applies to</th>
                  </tr>
                </thead>
                <tbody>
                  {RATES.map((r) => (
                    <tr key={r.key} className="border-b border-slate-100 last:border-0">
                      <td className="px-5 py-2.5"><RateLabel rate={r} /></td>
                      <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-slate-900">{rateText(defaults && defaults[r.key])}</td>
                      <td className="hidden px-5 py-2.5 text-[13px] text-slate-500 sm:table-cell">{r.hint}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="border-t border-slate-100 px-5 py-2.5 text-[12.5px] text-slate-500">
              New staff profiles start with these rates. A change is saved as a new version with a start date and is pushed to every staff profile.
            </p>
          </Card>

          {/* Time rules */}
          <Card title="Time Rules" icon={Clock} tone="indigo" sub="The evening window, Monday to Friday only">
            <div className="space-y-4">
              <FormRow cols={3}>
                <Field label="Evening starts">
                  {(id) => (
                    <select id={id} className="input-field" value={rules.evening_start} disabled={!canSettings} onChange={(e) => setRules((r) => ({ ...r, evening_start: e.target.value }))}>
                      {TIME_OPTIONS.filter((o) => o.value !== '24:00').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )}
                </Field>
                <Field label="Evening ends">
                  {(id) => (
                    <select id={id} className="input-field" value={rules.evening_end} disabled={!canSettings} onChange={(e) => setRules((r) => ({ ...r, evening_end: e.target.value }))}>
                      {TIME_OPTIONS.filter((o) => o.value !== '00:00').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )}
                </Field>
                <div className="flex items-end">
                  <button type="button" className="btn-primary" onClick={saveRules} disabled={savingRules || !rulesDirty || !canSettings}
                    title={canSettings ? undefined : needsPermission('pay_rates.settings')}>
                    {savingRules ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : null}Save time rules
                  </button>
                </div>
              </FormRow>
              <ul className="grid grid-cols-1 gap-2 text-[13px] text-slate-600 sm:grid-cols-2">
                <li className="rounded-lg bg-slate-50 px-3 py-2"><span className="font-medium text-slate-800">Evening rule.</span> Only the hours from {timeLabel(rules.evening_start)} to {timeLabel(rules.evening_end)}, Monday to Friday, get the Weekday Evening rate.</li>
                <li className="rounded-lg bg-slate-50 px-3 py-2"><span className="font-medium text-slate-800">Order.</span> Public Holiday, then Sunday, then Saturday, then Weekday Evening, then Weekday. Weekends and public holidays have no evening rate.</li>
                <li className="rounded-lg bg-slate-50 px-3 py-2"><span className="font-medium text-slate-800">Public holidays.</span> The state of the client's address decides which public holidays count.</li>
                <li className="rounded-lg bg-slate-50 px-3 py-2"><span className="font-medium text-slate-800">Midnight and rounding.</span> Hours are split at midnight and counted on the day they fall on. Hours and km to 2 decimal places, amounts to the cent.</li>
              </ul>
            </div>
          </Card>

          {/* History */}
          <Card title="History" icon={History} tone="amber" sub="Every saved version of the default rates, read-only" bodyClass="">
            {history.length === 0 ? (
              <p className="px-5 py-6 text-center text-sm text-slate-500">No versions saved yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-[12px] font-semibold text-slate-500">
                      <th scope="col" className="px-5 py-2.5 text-left">Start date</th>
                      {RATES.map((r) => (
                        <th key={r.key} scope="col" className="px-3 py-2.5 text-right whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5"><span className={clsx('h-2 w-2 rounded-full', r.dot)} aria-hidden="true" />{r.label}</span>
                        </th>
                      ))}
                      <th scope="col" className="px-5 py-2.5 text-left">Saved by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((v, i) => (
                      <tr key={v.id || `${v.start_date}-${i}`} className="border-b border-slate-100 last:border-0">
                        <td className="px-5 py-2.5 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 font-medium text-slate-800"><CalendarClock size={13} className="text-slate-400" aria-hidden="true" />{dmy(v.start_date)}</span>
                          {defaults && v.id && defaults.id === v.id && <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">In force</span>}
                        </td>
                        {RATES.map((r) => <td key={r.key} className="px-3 py-2.5 text-right tabular-nums text-slate-700">{rateText(v[r.key])}</td>)}
                        <td className="px-5 py-2.5 text-[13px] text-slate-600 whitespace-nowrap">
                          {v.created_by_name || v.created_by_email || 'Rostery'}
                          {v.created_at && <span className="block text-[12px] text-slate-400">{dmy(v.created_at)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <p className="flex items-center gap-2 text-[12.5px] text-slate-500">
            <Users size={13} aria-hidden="true" />
            Each worker's own rates and rate history are on their profile, under Pay Rates.
          </p>
        </>
      )}

      <DefaultsDialog open={editOpen} current={defaults} onClose={() => setEditOpen(false)} onSaved={load} />
      <SwitchDialog open={switchOpen} onClose={() => setSwitchOpen(false)} onSwitched={afterSwitch} />
    </div>
  );
}
